import { type FC, useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Card,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  TablePagination,
  Typography,
} from "@mui/material";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import {
  DragDropContext,
  Draggable,
  Droppable,
  type DropResult,
} from "@hello-pangea/dnd";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { Player } from "@/shared/components/Player";
import {
  useChildrenContentsQuery,
  useUpdateContentNestedSortMutation,
  type ChildrenContentsItem,
} from "@/partner/entities/contents";
import {
  CollectionContentSortType,
  ContentsSortTypeType,
} from "@/types/graphql/partner.generated";

// Маппинг режима формы → сортировка списка (порт референсного contentSorts)
const CONTENT_SORTS: Record<CollectionContentSortType, ContentsSortTypeType> = {
  [CollectionContentSortType.ManualType]: ContentsSortTypeType.ManualType,
  [CollectionContentSortType.LatestType]:
    ContentsSortTypeType.PublishAtDescType,
  [CollectionContentSortType.OldestType]: ContentsSortTypeType.PublishAtAscType,
  [CollectionContentSortType.UpdatedType]:
    ContentsSortTypeType.PublishAtDescType,
};

const ROWS_PER_PAGE_OPTIONS = [5, 10, 20, 30, 40, 50];

interface ChildrenContentsProps {
  collectionId: string;
  /** Текущее (возможно несохранённое) значение селекта collectionContentSort */
  sort: CollectionContentSortType;
}

// Порт референсного ChildrenContents.js: состав коллекции в правой колонке
// формы; drag&drop-порядок доступен только при sort === MANUAL, мутация
// updateContentNestedSort уходит сразу (до сохранения формы) — сдвиг
// соседей делает бэкенд, локально переставляем список сами.
export const ChildrenContents: FC<ChildrenContentsProps> = ({
  collectionId,
  sort,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [items, setItems] = useState<ChildrenContentsItem[]>([]);
  // Клик по строке открывает превью-плеер над списком (референсный
  // openItemId → PlayerPreview)
  const [openItemId, setOpenItemId] = useState<string | null>(null);

  const { data, loading, refetch } = useChildrenContentsQuery({
    fetchPolicy: "cache-and-network",
    variables: {
      page,
      rowsPerPage,
      collectionId,
      sort: CONTENT_SORTS[sort],
    },
  });

  const count = data?.contents?.count ?? 0;

  // Локальная копия для мгновенной перестановки при drag&drop
  useEffect(() => {
    setItems(data?.contents?.nodes ?? []);
  }, [data?.contents?.nodes]);

  const [updateNestedSort] = useUpdateContentNestedSortMutation({
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: "error" });
      void refetch();
    },
  });

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;

    const destinationItem = items[result.destination.index];
    if (!destinationItem) return;

    // Целевая позиция — nestedSortValue элемента, на чьё место встали
    // (порт референсного onDragEnd); фолбэк на index+1 для элементов без
    // значения (добавлены до включения MANUAL).
    const sortValue =
      destinationItem.nestedSortValue ?? result.destination.index + 1;

    const reordered = [...items];
    const [moved] = reordered.splice(result.source.index, 1);
    if (!moved) return;
    reordered.splice(result.destination.index, 0, moved);
    setItems(reordered);

    void updateNestedSort({
      variables: {
        id: result.draggableId,
        input: { collectionId, sortValue },
      },
      // Значения nestedSortValue соседей сдвинул бэкенд — перечитываем
      refetchQueries: ["ChildrenContents"],
    });
  };

  if (loading && !data) return null;
  if (count === 0) return null;

  const isManual = sort === CollectionContentSortType.ManualType;
  const selectedContent = items.find((item) => item.id === openItemId);

  return (
    <Card elevation={0}>
      <Typography variant="h6" sx={{ px: 2, pt: 2 }}>
        {t("collections.children.title")}
      </Typography>
      {selectedContent?.playback?.hlsUrl && (
        <Box sx={{ overflow: "hidden", p: 1 }}>
          <Player
            key={selectedContent.id}
            src={String(selectedContent.playback.hlsUrl)}
            poster={
              selectedContent.preview?.url
                ? String(selectedContent.preview.url)
                : (selectedContent.playback.thumbnailUrl ?? null)
            }
            autoPlay={false}
            hotkeys={false}
          />
        </Box>
      )}
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="collection-children">
          {(provided) => (
            <List ref={provided.innerRef} {...provided.droppableProps}>
              {items.map((content, index) => (
                <Draggable
                  key={content.id}
                  draggableId={content.id}
                  index={index}
                  isDragDisabled={!isManual}
                >
                  {(draggableProvided, snapshot) => (
                    <ListItem
                      ref={draggableProvided.innerRef}
                      {...draggableProvided.draggableProps}
                      sx={{
                        bgcolor: snapshot.isDragging
                          ? "action.hover"
                          : "background.paper",
                      }}
                      divider={index + 1 !== items.length}
                      secondaryAction={
                        isManual ? (
                          <IconButton
                            {...draggableProvided.dragHandleProps}
                            edge="end"
                          >
                            <DragHandleIcon />
                          </IconButton>
                        ) : undefined
                      }
                      disablePadding
                    >
                      <ListItemButton onClick={() => setOpenItemId(content.id)}>
                        <ListItemAvatar>
                          <Avatar
                            variant="rounded"
                            src={
                              content.preview?.url
                                ? String(content.preview.url)
                                : content.playback?.thumbnailUrl
                                  ? String(content.playback.thumbnailUrl)
                                  : undefined
                            }
                            sx={{ width: 64, height: 36, mr: 1 }}
                          />
                        </ListItemAvatar>
                        <ListItemText
                          primary={content.title}
                          secondary={
                            content.publishAt
                              ? t("videos.item.publishedAtDate", {
                                  publishAt: content.publishAt,
                                })
                              : t("videos.item.notPublished")
                          }
                          primaryTypographyProps={{ noWrap: true }}
                        />
                      </ListItemButton>
                    </ListItem>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </List>
          )}
        </Droppable>
      </DragDropContext>
      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={(_event, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(Number(event.target.value));
          setPage(0);
        }}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        labelRowsPerPage={t("common.pagination.rowsPerPage")}
        labelDisplayedRows={({ from, to, count }) =>
          t("common.pagination.displayedRows", {
            from,
            to,
            count:
              count !== -1 ? count : t("common.pagination.moreThan", { to }),
          })
        }
      />
    </Card>
  );
};
